// DailyCommande.js

import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  TextField,
  useTheme,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
} from "@mui/material";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import Header from "components/Header";
import Popup from "components/Popup";
import { Link } from "react-router-dom";
import { DeleteOutline, Email, MonetizationOn } from "@material-ui/icons";
import { useGetUserQuery } from "state/api";
import axios from "axios";
import { server } from "server";
import "./commande.css";
import FormMainCommand from "scenes/formMainCommand";
import { toast } from "react-toastify";
import FlexBetween from "components/FlexBetween";
import { useSelector } from "react-redux";
import PopupComptabiliteCommande from "components/PopupComptabiliteCommande";
import { IconButton } from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import StatBox from "components/StatBox";

const DailyCommande = () => {
  const theme = useTheme();
  const userId = useSelector((state) => state.global.userId);
  const { data } = useGetUserQuery(userId);
  const [selectedDate, setSelectedDate] = useState("");
  const [loading, setLoading] = useState(false);
  const [prestations, setPrestations] = useState([]);
  const [bestCustomers, setBestCustomers] = useState([]);
  const [openPopup, setOpenPopup] = useState(false);
  const [currentView, setCurrentView] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [dailyCash, setDailyCash] = useState(0);
  const [dailyDepenses, setDailyDepenses] = useState(0);
  const [id, setId] = useState(null);
  const [openPopup2, setOpenPopup2] = useState(false);
  const [priceRange, setPriceRange] = useState("");

  useEffect(() => {
    if (currentView === "bestCustomers") {
      fetchBestCustomers();
    } else {
      fetchData();
    }
  }, [currentView, selectedDate]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch daily cash
        const dailyCash = await axios.get(
          `${server}/payment-commande/daily-paid-amount-cash`
        );
        setDailyCash(dailyCash.data.netCashAmount);

        const dailyResponseDepense = await axios.get(
          `${server}/commande/api/commande-depense/daily`
        );
        setDailyDepenses(dailyResponseDepense.data.total);
      } catch (err) {
        console.error(err);
      }
    };

    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      let url = `${server}/commande/all-commandes-with-totals`;
      if (currentView === "all") {
        url = `${server}/commande/all-commandes-with-totals`;
        if (selectedDate) {
          url = `${server}/commande/commandes-by-date-with-totals/${selectedDate}`;
        }
      } else if (currentView === "debts") {
        if (selectedDate) {
          url = `${server}/commande/commandes-with-reste-by-date-with-totals/${selectedDate}`;
        } else {
          url = `${server}/commande/commandes-with-reste-with-totals`;
        }
      }
      const response = await axios.get(url);
      const data = response?.data?.commandes || [];
      setPrestations(data);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching data:", error);
      setLoading(false);
    }
  };

  const fetchBestCustomers = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${server}/commande/best-customers`);
      const data = response.data || [];
      setBestCustomers(data);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching best customers:", error);
      setLoading(false);
    }
  };

  const formatNumber = (number) => {
    return Number(number || 0).toLocaleString();
  };

  const handleDelete = async (id) => {
    try {
      if (window.confirm("Veux-tu supprimer cette commande?")) {
        const response = await axios.delete(
          `${server}/commande/delete-commande/${id}`
        );
        if (response) {
          toast.success("Commande supprimée avec succès");
          fetchData();
        } else {
          toast.error("Erreur lors de la suppression de la commande");
        }
      }
    } catch (error) {
      console.log(error);
      toast.error("Utilisateur introuvable");
    }
  };

  const columns = [
    {
      field: "nomClient",
      headerName: "Nom Client",
      flex: 2,
    },
    {
      field: "totalQty",
      headerName: "Quantité",
      flex: 1,
      valueGetter: (params) => formatNumber(params.row.totalQty),
    },
    {
      field: "totalPriceUnitaire",
      headerName: "Prix Unitaire",
      flex: 1.5,
      valueGetter: (params) => formatNumber(params.row.totalPriceUnitaire),
    },
    {
      field: "totalPriceTotal",
      headerName: "Prix Total",
      flex: 1.5,
      valueGetter: (params) => formatNumber(params.row.totalPriceTotal),
    },
    {
      field: "totalAvance",
      headerName: "Paiement",
      flex: 1.5,
      valueGetter: (params) => formatNumber(params.row.totalAvance),
    },
    {
      field: "totalReste",
      headerName: "Reste",
      flex: 1,
      valueGetter: (params) => formatNumber(params.row.totalReste),
    },
    {
      field: "action",
      headerName: "Action",
      width: 230,
      renderCell: (params) => {
        return (
          <>
            <IconButton
              onClick={() => {
                setOpenPopup2(true);
                setId(params.row.id);
              }}
            >
              <VisibilityIcon />
            </IconButton>

            <Link to={`/commande-details/${params.row.id}`}>
              <Button variant="contained" color="primary" size="small">
                Détails
              </Button>
            </Link>
            <Link to={`/commande-payment/${params.row.id}`}>
              <Button variant="contained" color="secondary" size="small">
                Paiement
              </Button>
            </Link>
            {data?.role === "superadmin" && (
              <DeleteOutline
                className="userListDelete"
                onClick={() => handleDelete(params.row.id)}
              />
            )}
          </>
        );
      },
    },
  ];

  const bestCustomersColumns = [
    {
      field: "nomClient",
      headerName: "Nom Client",
      flex: 2,
      valueGetter: (params) => params.row._id?.nomClient || "",
    },
    {
      field: "contact",
      headerName: "Contact",
      flex: 1,
      valueGetter: (params) => params.row._id?.contact || "",
    },
    {
      field: "totalAvance",
      headerName: "Total Avance",
      flex: 2,
      valueGetter: (params) => formatNumber(params.row.totalAvance || 0),
    },
  ];

  const handleSearch = (event) => {
    setSearchQuery(event.target.value);
  };

  const handlePriceRangeChange = (event) => {
    setPriceRange(event.target.value);
  };

  const filterByPriceRange = (commande) => {
    const totalPriceTotal = commande.totalPriceTotal || 0;

    switch (priceRange) {
      case "0-1M":
        return totalPriceTotal >= 0 && totalPriceTotal <= 1000000;
      case "1M-5M":
        return totalPriceTotal > 1000000 && totalPriceTotal <= 5000000;
      case "5M-10M":
        return totalPriceTotal > 5000000 && totalPriceTotal <= 10000000;
      case "10M-15M":
        return totalPriceTotal > 10000000 && totalPriceTotal <= 15000000;
      case "15M-20M":
        return totalPriceTotal > 15000000 && totalPriceTotal <= 20000000;
      case "20M-25M":
        return totalPriceTotal > 20000000 && totalPriceTotal <= 25000000;
      case "25M+":
        return totalPriceTotal > 25000000;
      default:
        return true;
    }
  };

  const filteredPrestations = prestations
    .filter((commande) => {
      return (
        (commande.nomClient &&
          commande.nomClient
            .toLowerCase()
            .includes(searchQuery.toLowerCase())) ||
        (commande.contact &&
          commande.contact.toLowerCase().includes(searchQuery.toLowerCase()))
      );
    })
    .filter(filterByPriceRange);

  const filteredBestCustomers = bestCustomers.filter((customer) => {
    const nomClient = customer._id?.nomClient || "";
    const contact = customer._id?.contact || "";
    return (
      nomClient.toLowerCase().includes(searchQuery.toLowerCase()) ||
      contact.toLowerCase().includes(searchQuery.toLowerCase())
    );
  });

  const handleDateChange = (event) => {
    const date = event.target.value;
    setSelectedDate(date);
  };

  return (
    <Box m="1.5rem 2.5rem">
      <Header
        title="Les commandes journalières"
        subtitle="La liste des commandes journalières"
      />
      <FlexBetween>
        <Box
          flex="1 1 300px"
          style={{ marginRight: "8px", marginBottom: "20px" }}
        >
          <StatBox
            title="L'argent disponible dans la caisse aujourd’hui"
            value={dailyCash}
            increase="fbu"
            description="Aujourd’hui"
            icon={
              <MonetizationOn
                sx={{ color: theme.palette.secondary[300], fontSize: "26px" }}
              />
            }
          />
        </Box>
        <br />
        <Box flex="1 1 300px" style={{ marginBottom: "20px" }}>
          <StatBox
            title="Depense journaliere"
            increase="fbu"
            value={dailyDepenses}
            description="Depuis 7h30"
            icon={
              <>
                {" "}
                <Email
                  sx={{ color: theme.palette.secondary[300], fontSize: "26px" }}
                />
              </>
            }
          />
        </Box>
      </FlexBetween>
      <FlexBetween>
        <Box>
          <Button
            variant={currentView === "all" ? "contained" : "outlined"}
            color="secondary"
            sx={{ mr: 1 }}
            onClick={() => setCurrentView("all")}
          >
            Toutes les commandes
          </Button>

          <Link to="/Dette">
            <Button variant={"outlined"} color="secondary" sx={{ mr: 1 }}>
              Commandes en dette
            </Button>
          </Link>
          <Link to="/payment-recent">
            <Button variant={"outlined"} color="secondary" sx={{ mr: 1 }}>
              Payement recent
            </Button>
          </Link>
          <Button
            variant={currentView === "bestCustomers" ? "contained" : "outlined"}
            color="success"
            onClick={() => setCurrentView("bestCustomers")}
          >
            Meilleurs clients
          </Button>
        </Box>
        <TextField
          id="search"
          label="Rechercher"
          type="text"
          value={searchQuery}
          onChange={handleSearch}
          sx={{ width: "300px", marginRight: "1rem" }}
        />
        <FormControl sx={{ width: "250px" }}>
          <InputLabel id="price-range-label">Filtrer par Prix Total</InputLabel>
          <Select
            labelId="price-range-label"
            value={priceRange}
            label="Filtrer par Prix Total"
            onChange={handlePriceRangeChange}
          >
            <MenuItem value="">Tous</MenuItem>
            <MenuItem value="0-1M">0 - 1 million</MenuItem>
            <MenuItem value="1M-5M">1 million - 5 million</MenuItem>
            <MenuItem value="5M-10M">5 million - 10 million</MenuItem>
            <MenuItem value="10M-15M">10 million - 15 million</MenuItem>
            <MenuItem value="15M-20M">15 million - 20 million</MenuItem>
            <MenuItem value="20M-25M">20 million - 25 million</MenuItem>
            <MenuItem value="25M+">30 million et plus</MenuItem>
          </Select>
        </FormControl>
        <TextField
          id="date"
          label="Sélectionner la Date"
          type="date"
          value={selectedDate}
          onChange={handleDateChange}
          InputLabelProps={{
            shrink: true,
          }}
          sx={{ width: "250px", marginLeft: "1rem" }}
        />
      </FlexBetween>
      {loading ? (
        <Box display="flex" justifyContent="center" mt="2rem">
          <CircularProgress />
        </Box>
      ) : currentView === "bestCustomers" ? (
        <Box
          height="80vh"
          sx={{
            "& .MuiDataGrid-root": {
              border: "none",
            },
            "& .MuiDataGrid-cell": {
              borderBottom: "none",
            },
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: theme.palette.background.alt,
              color: theme.palette.secondary[100],
              borderBottom: "none",
            },
            "& .MuiDataGrid-virtualScroller": {
              backgroundColor: theme.palette.primary.light,
            },
            "& .MuiDataGrid-footerContainer": {
              backgroundColor: theme.palette.background.alt,
              color: theme.palette.secondary[100],
              borderTop: "none",
            },
            "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
              color: `${theme.palette.secondary[200]} !important`,
            },
          }}
        >
          {data?.role === "superadmin" ? (
            <DataGrid
              rows={filteredBestCustomers.map((customer, index) => ({
                id: index,
                ...customer,
              }))}
              columns={bestCustomersColumns}
              pageSize={8}
              components={{ Toolbar: GridToolbar }}
            />
          ) : (
            <h3 style={{ color: "red" }}>
              Ces informations sont réserver pour le superadmin
            </h3>
          )}
        </Box>
      ) : (
        <Box
          height="80vh"
          sx={{
            "& .MuiDataGrid-root": {
              border: "none",
            },
            "& .MuiDataGrid-cell": {
              borderBottom: "none",
            },
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: theme.palette.background.alt,
              color: theme.palette.secondary[100],
              borderBottom: "none",
            },
            "& .MuiDataGrid-virtualScroller": {
              backgroundColor: theme.palette.primary.light,
            },
            "& .MuiDataGrid-footerContainer": {
              backgroundColor: theme.palette.background.alt,
              color: theme.palette.secondary[100],
              borderTop: "none",
            },
            "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
              color: `${theme.palette.secondary[200]} !important`,
            },
          }}
        >
          <DataGrid
            rows={filteredPrestations.map((commande) => ({
              id: commande._id,
              nomClient: commande.nomClient,
              commande: commande.prestations[0]?.commande || "",
              totalQty: commande.totalQty,
              totalPriceUnitaire: commande.totalPriceUnitaire,
              totalPriceTotal: commande.totalPriceTotal,
              totalAvance: commande.totalAvance,
              totalReste: commande.totalReste,
              desc: commande.prestations[0]?.desc || "",
              createdAt: commande.createdAt
                ? commande.createdAt.slice(0, 10)
                : "",
              prestations: commande.prestations,
            }))}
            columns={columns}
            pageSize={8}
            components={{ Toolbar: GridToolbar }}
          />
        </Box>
      )}
      <Popup
        title="Créer une nouvelle commande"
        openPopup={openPopup}
        setOpenPopup={setOpenPopup}
      >
        <FormMainCommand setOpenPopup={setOpenPopup} />
      </Popup>
      <Popup
        title="Détails de la Commande"
        openPopup={openPopup2}
        setOpenPopup={setOpenPopup2}
      >
        <PopupComptabiliteCommande
          setOpenPopup={setOpenPopup2}
          commandeId={id}
        />
      </Popup>
    </Box>
  );
};

export default DailyCommande;
